import {
    CompleteConsentForm,
    FormDefinitionType,
    IEConsentForm,
    Patient,
} from '@/api/dtos';
import useCompleteConsentForm from '@/hooks/form/useCompleteConsentForm';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { FormContext, FormDefinitionContext, IFormContext, PatientContext } from '@ngt/opms';
import { useSnackbar } from 'notistack';
import { FC, FormEvent, useCallback, useContext, useState } from 'react';


export interface ConsentCompleteActionProps {
    formDefinitionId: FormDefinitionType;
}

const ConsentCompleteAction: FC<ConsentCompleteActionProps> = ({
    formDefinitionId,
}) => {
    const actionLabel = 'Complete';
    const { enqueueSnackbar } = useSnackbar();
    const { data: untypedPatient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const patient = untypedPatient as Patient | undefined;
    const formName = formDefinition?.name ?? 'Consent Form';
    const { data: form, refresh: refreshForm } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [requestError, setRequestError] = useState<string>();

    const completeConsent = useCompleteConsentForm({
        revalidateForm: refreshForm
    });

    const close = useCallback(() => {
        if (!saving) {
            setOpen(false);
        }
    }, [saving]);

    const showDialog = useCallback(() => {
        setRequestError(undefined);
        setOpen(true);
    }, [form, patient]);

    const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setRequestError(undefined);

        if (!patient?.id) {
            return;
        }

        setSaving(true);

        try {
            await completeConsent(new CompleteConsentForm({
                patientId: patient.id,
                formDefinitionId: formDefinitionId
            }));

            setOpen(false);
            enqueueSnackbar(`${formName} completed successfully.`, { variant: 'success' });
        }
        catch (error: any) {
            setRequestError(
                error?.responseStatus?.message ??
                error?.message ??
                `${formName} could not be completed.`
            );
        }
        finally {
            setSaving(false);
        }
    }, [
        completeConsent,
        patient,
        formDefinitionId,
        enqueueSnackbar,
        formName
    ]);

    return (
        <>
            <Button
                variant="contained"
                color={'success'}
                onClick={showDialog}
                disabled={!patient?.id}
            >
                {actionLabel}
            </Button>

            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <Box component="form" onSubmit={submit} noValidate>
                    <DialogTitle>{actionLabel} {formName}</DialogTitle>
                    <DialogContent>
                        {requestError && <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>}

                        Are you sure you want to complete this consent form?
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={close} disabled={saving}>Cancel</Button>
                        <Button type="submit" variant="contained" color='success' loading={saving}>
                            {actionLabel}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};

export default ConsentCompleteAction;
